import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Polygon, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';

import PollWindow from './PollWindow/PollWindow';
import Fader from '../Loaders/Fader';

import { saveWardHexagons, loadWardHexagons } from '../../../util/LocalDb';
import { getAllWardShapes, getWardData } from '../../../http/wardsAPI';

import './Map.scss';

const CACHE_DAYS = 5;
const CACHE_TTL = CACHE_DAYS * 24 * 60 * 60 * 1000; 

const FitBounds = ({ polygons }) => {
  const map = useMap();

  useEffect(() => {
    if (!polygons || polygons.length === 0) return;

    const allCoords = polygons.flatMap((polygon) => polygon.coordinates);

    const bounds = L.latLngBounds(allCoords);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [polygons, map]);

  return null;
};

const WardMap = ({ wardId }) => {
  const config = useSelector((state) => state.config.config);

  const [polygons, setPolygons] = useState([]);
  const [activePoll, setActivePoll] = useState({ id: 0, coordinates: [], title: '' });
  const [ridingPoly, setRidingPoly] = useState([]);
  const [cp, setCp] = useState([]);
  const [mapData, setMapData] = useState(false);
  const [polygonData, setPolygonData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchOrLoadData = async () => {
    try {
      const cached = await loadWardHexagons();
      const now = Date.now();
      if (cached && cached.data && cached.timestamp && (now - cached.timestamp < CACHE_TTL)) {
        setPolygons(cached.data);
      } else {
        const res = await getAllWardShapes(config);
        const data = res?.data?.rows || [];
        setPolygons(data);
        await saveWardHexagons({ data, timestamp: now });
      }
    } catch (error) {
      console.error('Error fetching or loading polygons:', error);
    }
  };

  const fetchOrLoadRidingData = async () => {
    try {
      const res = await getWardShapes(config, wardId);

      setPolygons(res.data.rows.riding_polls);
      setCp(res.data.rows.riding[0].cp);
      setRidingPoly(res.data.rows.riding[0].coordinates);
      setActivePoll(res.data.rows.poll[0]);

      setMapData(true);
    } catch (err) {
      console.error('Error fetching or loading polygons for riding:', err);
    }
  };

  useEffect(() => {
    if (!config) {
      setTimeout(() => {
        if (wardId) {
          fetchOrLoadRidingData();
        } else {
          fetchOrLoadData();
        }
      }, 1000);
    }

    if (wardId) {
      fetchOrLoadRidingData();
    } else {
      fetchOrLoadData();
    }
  }, [config, wardId]);

  const handlePolygonClick = async (polygonId) => {
    try {
      setIsModalOpen(true);
      const res = await getWardData(config, polygonId);
      setPolygonData(res?.data?.demographics || null);
    } catch (err) {
      console.error(`Failed to load ward data ${polygonId}:`, err);
    }
  };

  const closePollWindow = () => {
    setIsModalOpen(false);
    setPolygonData(null);
  };

  if (wardId && !mapData) {
    return null;
  }

  return (
    <div>
      <div>
        {polygons.length > 0 ? (
          <div className="polygon-map">
            <MapContainer style={{ width: '100%', height: '100%' }} zoom={13} center={cp.length > 0 ? cp : [0, 0]}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {ridingPoly.length > 0 ? <Polygon positions={ridingPoly} pathOptions={{ color: 'red' }} /> : ''}
              {polygons.map((polygon) => (
                <Polygon
                  key={polygon.id}
                  positions={polygon.coordinates}
                  pathOptions={!wardId || (wardId && polygon.id === activePoll.id) ? {} : { color: 'rgba(0,0,0,0.23)' }}
                  eventHandlers={{ click: () => handlePolygonClick(polygon.id) }}
                >
                  <Tooltip sticky>{polygon.title || `Polygon ${polygon.id}`}</Tooltip>
                </Polygon>
              ))}
              <FitBounds polygons={polygons} />
            </MapContainer>
          </div>
        ) : (
          <div className="sentiment-charts">
            <Fader />
          </div>
        )}
      </div>

      {isModalOpen && <PollWindow data={polygonData} isOpen={isModalOpen} onCloseHandler={closePollWindow} />}
    </div>
  );
};

export default WardMap;
